import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import { PharmacyPageHeader } from '../ui';
import StockTable from '../components/StockTable';
import { useInventoryRoutes } from '../components/inventoryUi';
import {
  buildInventorySiteOptions,
  parseInventorySiteKey
} from '../components/inventorySites';

const copy = PHARMACY_COPY.STOCK_SITES;

export default function PharmacySiteStocksPage() {
  const { workSites, workSiteId, centralSite, mode } = usePharmacy();
  const navigate = useNavigate();
  const routes = useInventoryRoutes();
  const siteOnly = mode === 'site';
  const [selectedKey, setSelectedKey] = useState('');

  const inventorySites = useMemo(
    () => buildInventorySiteOptions(workSites, centralSite, PHARMACY_COPY.INVENTORY.LOCATION_DUTY),
    [workSites, centralSite]
  );

  useEffect(() => {
    if (siteOnly) {
      setSelectedKey(workSiteId ? String(workSiteId) : '');
      return;
    }
    if (selectedKey) {
      const stillValid = inventorySites.some((s) => s.key === selectedKey);
      if (stillValid) return;
    }
    if (workSiteId) setSelectedKey(String(workSiteId));
    else if (inventorySites[0]) setSelectedKey(inventorySites[0].key);
  }, [workSiteId, inventorySites, selectedKey, siteOnly]);

  const parsed = useMemo(() => parseInventorySiteKey(selectedKey), [selectedKey]);
  const siteId = siteOnly ? (workSiteId ? String(workSiteId) : '') : parsed.siteId || '';
  const isDuty = !siteOnly && parsed.isDuty;
  const currentOption = useMemo(
    () => inventorySites.find((s) => s.key === String(selectedKey)) || null,
    [inventorySites, selectedKey]
  );
  const currentSite = currentOption?.site || null;

  const isCentralMain =
    Boolean(centralSite && String(centralSite.id) === String(siteId)) && !isDuty;
  const allowAdjust = isCentralMain && !siteOnly;
  const siteLabel = currentOption?.name || (siteId ? `Site #${siteId}` : '');

  if (siteOnly && !workSiteId) {
    return (
      <div className="cp-stock-page">
        <PharmacyPageHeader badge={copy.BADGE_WORK} title={copy.TITLE_WORK} description={copy.DESC_WORK} />
        <div className="cp-empty-state cp-card">
          <span className="cp-empty-state__icon" aria-hidden>
            <Icon name="building" size={28} />
          </span>
          <h3 className="cp-empty-state__title">{copy.NO_WORK_SITE}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-stock-page">
      <PharmacyPageHeader
        badge={siteOnly ? copy.BADGE_WORK : copy.BADGE}
        title={siteOnly ? copy.TITLE_WORK : copy.TITLE}
        description={siteOnly ? copy.DESC_WORK : copy.DESC}
        actions={
          <button
            type="button"
            className="cp-btn cp-btn--ghost"
            onClick={() =>
              navigate(
                selectedKey
                  ? `${routes.LIST}?site=${encodeURIComponent(selectedKey)}`
                  : routes.LIST
              )
            }
          >
            <Icon name="clipboard-list" size={15} aria-hidden />
            {copy.CTA_INVENTORY}
          </button>
        }
      />

      {inventorySites.length === 0 ? (
        <div className="cp-empty-state cp-card">
          <span className="cp-empty-state__icon" aria-hidden>
            <Icon name="building" size={28} />
          </span>
          <h3 className="cp-empty-state__title">{copy.NO_SITE}</h3>
        </div>
      ) : (
        <>
          {!siteOnly ? (
            <div className="cp-site-picker" role="group" aria-label={copy.SITE_PICKER_ARIA}>
              {inventorySites.map((s) => {
                const active = String(s.key) === String(selectedKey);
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={`cp-site-picker__card${active ? ' is-active' : ''}${
                      s.isCentral ? ' is-central' : ''
                    }`}
                    onClick={() => setSelectedKey(String(s.key))}
                    aria-pressed={active}
                  >
                    <span className="cp-site-picker__icon" aria-hidden>
                      <Icon name={s.isDuty ? 'shield' : s.isCentral ? 'warehouse' : 'building'} size={16} />
                    </span>
                    <span className="cp-site-picker__body">
                      <span className="cp-site-picker__name">{s.name}</span>
                      {s.isCentral ? <span className="cp-site-picker__tag">{copy.CENTRAL_TAG}</span> : null}
                      {s.isDuty ? <span className="cp-site-picker__tag">{copy.DUTY_TAG}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : currentSite ? (
            <div className="cp-stock-worksite-chip" aria-label={currentSite.name}>
              <Icon name="warehouse" size={16} aria-hidden />
              <strong>{currentSite.name}</strong>
            </div>
          ) : null}

          {siteId ? (
            <StockTable
              key={`stock-${selectedKey}`}
              siteId={siteId}
              allowAdjust={allowAdjust}
              siteLabel={siteLabel}
              siteInventoryOnly={!isCentralMain || siteOnly}
              stockLocation={isDuty ? 'DUTY' : 'MAIN'}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
